import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { IconButton, Skeleton, SvgIcon, TableSortLabel, Typography, useTheme } from '@mui/material';
import { User } from '../../../../app/services/api/generated';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import useLogError from '../../../../hooks/useLogError';
import CustomPagination from '../../../../components/forms/Pagination';
import { Trash01 } from '../../../../assets';
import CreateUpdateUserModal from './CreateUpdateUserModal';
import { UpsertUserRequest } from '../types/UpsertUserRequest';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { ConfirmationDeleteDialog } from '../../../../components/shared/dialogs/ConfirmationDeleteDialog';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import { UserContext } from '../../../../components/shared/useUser';

interface UsersTableProps {
  list: User[];
  loadData: () => void;
  isLoading: boolean;
  searchTerm: string;
  clearAll: () => void;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  onCreate: () => void;
}

const UsersTable = ({ list, loadData, isLoading, searchTerm, clearAll, page, setPage, onCreate }: UsersTableProps) => {
  const { t } = useTranslation('manageUsers');
  const { logError } = useLogError();
  const theme = useTheme();
  const { deleteUser, editUser } = useNswagClient();
  const [data, setData] = React.useState(list);
  const [selectedUser, setSelectedUser] = React.useState<User | undefined>(undefined);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof User>('emailAddress');
  const [showDeleteSuccessMessage, setshowDeleteSuccessMessage] = React.useState(false);
  const [showUpdateSuccessMessage, setShowUpdateSuccessMessage] = React.useState(false);
  const [showResetPasswordSuccessMessage, setShowResetPasswordSuccessMessage] = React.useState(false);
  const { hasPermissionTo } = React.useContext(UserContext);

  const rowsPerPage = 10;
  const maxPages = list
    ? Math.max(1, Math.ceil(list.length / rowsPerPage))
    : 1;

  React.useEffect(() => {
    setData(list);
    handleSort('emailAddress');
  }, [list]);

  const visibleRows = () => {
    return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  const handleSort = (property: keyof User) => {
    const isAsc = orderBy === property && order === 'asc';
    if (data) {
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property as keyof User);
      handleSearchChange(property);
    }
  };

  const handleSearchChange = (property: keyof User) => {
    setData(sortedData(property));
  };

  const sortedData = (property: keyof User) => {
    const isAsc = order === 'asc';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const comparator = (a: any, b: any) => {
      if (property === 'persona') {
        const descriptionA = a.persona?.description ?? '';
        const descriptionB = b.persona?.description ?? '';
        return isAsc ? descriptionA.localeCompare(descriptionB) : descriptionB.localeCompare(descriptionA);
      }

      if (typeof a[orderBy] === 'string') {
        return (isAsc ? a[orderBy]?.localeCompare(b[orderBy]) : b[orderBy]?.localeCompare(a[orderBy]));
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (isAsc ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy]) as any;
      }
    };

    return list.sort(comparator);
  };

  const onEdit = (user: User | undefined) => {
    if (user) {
      setSelectedUser(user);
    }
    setShowEditModal(true);
  };

  const handleEdit = (body: UpsertUserRequest) => {
    editUser(selectedUser?.id ?? 0, body)
      .then(() => {
        setShowUpdateSuccessMessage(true);
        setTimeout(() => loadData(), 2000);
      })
      .catch(logError);
  };

  const onDelete = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleDelete = () => {
    deleteUser(selectedUser?.id ?? 0)
      .then(() => {
        setshowDeleteSuccessMessage(true);
        loadData();
      })
      .catch(logError);

    setShowDeleteModal(false);
    setSelectedUser(undefined);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedUser(undefined);
  };

  if (isLoading && data.length === 0) {
    return <Skeleton sx={{ bgcolor: theme.palette.custom.gray[100] }}
      variant='rectangular'
      height={445.89} />;
  }

  if (!isLoading && data.length === 0) {
    return <EmptyTable
      text={t('users')}
      searchTerm={searchTerm}
      onClear={clearAll}
      onAdd={onCreate}
      addItemText={t('newUser')}
      onAddIsCreating={false} />;
  }

  return (
    <>
      {isLoading ? <Skeleton sx={{ bgcolor: theme.palette.custom.gray[100] }}
        variant='rectangular'
        height={445.89} />
        :
        <TableContainer sx={{ borderRadius: '12px' }}
          component='div'>
          <Table size="small">
            <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
              <TableRow>
                <TableCell width={'20%'}>
                  <TableSortLabel
                    active={orderBy === 'emailAddress'}
                    direction={order}
                    onClick={() => handleSort('emailAddress')}
                  >
                    {t('email')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'franchiseName'}
                    direction={order}
                    onClick={() => handleSort('franchiseName')}
                  >
                    {t('company')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'persona'}
                    direction={order}
                    onClick={() => handleSort('persona')}
                  >
                    {t('role')}
                  </TableSortLabel>
                </TableCell>
                <TableCell width={'10%'} />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows().map((row) => (
                <TableRow
                  key={row.id}
                  onClick={hasPermissionTo(['UserManagementUpdate']) ? () => onEdit(row) : undefined}
                  sx={hasPermissionTo(['UserManagementUpdate']) ? { cursor: 'pointer' } : {}}
                >
                  <TableCell>
                    <Typography variant='textXS'
                      fontWeight={600}
                      color={theme.palette.primary[500]}
                      sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      {row.emailAddress}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS'>
                      {row.franchiseName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS'>
                      {row.persona?.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      disabled={!hasPermissionTo(['UserManagementDelete'])}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(row);
                      }}>
                      <SvgIcon fontSize='small'><Trash01 color={theme.palette.error[500]} /></SvgIcon>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {/* Add empty rows to maintain height */}
              {Array.from({ length: Math.max(0, 10 - (visibleRows().length ?? 0)) }).map((_, index) => (
                <TableRow key={`empty-row-${index}`}
                  sx={{ height: '40px', backgroundColor: theme.palette.common.white }}>
                  <TableCell colSpan={6}
                    sx={{
                      backgroundColor: theme.palette.common.white,
                      borderColor: index === Math.max(0, 10 - (visibleRows().length ?? 0)) - 1 ? theme.palette.custom.gray[100] : theme.palette.common.white,
                    }} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>}
      <CustomPagination
        page={page}
        setPage={setPage}
        maxPages={maxPages}
        breakpointForChangeDisplay={120000}
      />
      <CreateUpdateUserModal
        openModal={showEditModal}
        onConfirm={handleEdit}
        editUser={selectedUser}
        onCancel={handleCloseModal}
        onResetPassword={() => setShowResetPasswordSuccessMessage(true)}
      />
      <ConfirmationDeleteDialog
        open={showDeleteModal}
        onConfirm={handleDelete}
        onCancel={handleCloseModal}
        nameOfObject={t('user')}
      />
      <MessagesSnackbar
        open={showDeleteSuccessMessage}
        onClose={() => setshowDeleteSuccessMessage(false)}
        message={t('userDeleted')}
        severity="success"
        duration={null}
      />
      <MessagesSnackbar
        open={showUpdateSuccessMessage}
        onClose={() => setShowUpdateSuccessMessage(false)}
        message={t('successUpdateMessage')}
        severity="success"
        duration={null}
      />
      <MessagesSnackbar
        open={showResetPasswordSuccessMessage}
        onClose={() => setShowResetPasswordSuccessMessage(false)}
        message={t('resetEmailSent')}
        severity="success"
        duration={null}
      />
    </>
  );
};

export default UsersTable;
